import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import type { Me } from "../../../shared/api.ts";
import { api, queryKeys } from "./api.ts";
import { HttpError, setCsrfToken } from "./http.ts";

/** `GET /api/me`, with 401 turned into null so the router can send the visitor to `/login`. */
export async function fetchMe(): Promise<Me | null> {
  try {
    const me = await api.me();
    setCsrfToken(me.csrfToken);
    return me;
  } catch (error) {
    if (error instanceof HttpError && error.status === 401) {
      setCsrfToken(null);
      return null;
    }
    throw error;
  }
}

export function useMe() {
  return useQuery({ queryKey: queryKeys.me, queryFn: fetchMe, staleTime: 60_000 });
}

/** The signed-in tenant's model access. The composer and the access notice read it. */
export function useModelAccess(enabled = true) {
  return useQuery({ queryKey: queryKeys.modelAccess, queryFn: api.modelAccess, staleTime: 30_000, enabled });
}

/** The signed-in user. Only call below a route guard that waited for `useMe`. */
export function useCurrentUser(): Me {
  const { data } = useMe();
  if (!data) {
    throw new Error("useCurrentUser needs a signed-in user");
  }
  return data;
}

export function useSessions() {
  return useQuery({
    queryKey: queryKeys.sessions,
    queryFn: api.sessions,
    staleTime: 15_000,
    // Keep the running marks fresh for sessions that are not open.
    refetchInterval: (query) => (query.state.data?.some((session) => session.running) ? 8_000 : false),
  });
}

export function useAuthConfig() {
  return useQuery({ queryKey: queryKeys.authConfig, queryFn: api.authConfig, staleTime: Infinity, retry: false });
}

/** Sets `document.title` to "<page> · Графа 33". */
export function useDocumentTitle(title: string | null | undefined, brand: string): void {
  useEffect(() => {
    document.title = title ? `${title} · ${brand}` : brand;
  }, [title, brand]);
}
